/**
 * @fileoverview FireSquirrel-authored dimensional artwork for catalog gaps.
 * @module ui-artwork/catalog/gapAssets
 *
 * Responsibilities: Register original 3D gap artwork with stable IDs.
 * Dependencies: ArtworkCatalogEntry.
 * Architecture: Static package catalog metadata with locally authored SVG bodies.
 * Notes: All gap artwork is bundled and has no remote asset references.
 */
import type { ArtworkCatalogEntry, ArtworkCategory } from '../types';

const make = (id: string, label: string, category: ArtworkCategory, keywords: string[], policy: ArtworkCatalogEntry['colorPolicy'] = 'customizable'): ArtworkCatalogEntry => ({
  id: `art:3d/${id}`,
  label,
  category,
  style: '3d',
  keywords: [label.toLowerCase(), ...keywords],
  colorPolicy: policy,
  defaultColor: policy === 'controlled-hazard' ? '#dc2626' : '#2563eb',
  ...(policy === 'controlled-hazard' ? {
    allowedHazardColors: [
      { hex: '#dc2626', label: 'Flammable gas', standard: 'DOT 2.1' },
      { hex: '#f97316', label: 'Warning border', standard: 'NAPSG' },
    ],
  } : {}),
  source: 'custom-3d-gap',
});

export const GAP_3D_ARTWORK: ArtworkCatalogEntry[] = [
  make('apartment-complex', 'Apartment complex', 'entity', ['apartment', 'multifamily', 'residential']),
  make('duplex-multifamily', 'Duplex multifamily home', 'entity', ['duplex', 'triplex', 'multifamily']),
  make('warehouse-distribution', 'Warehouse distribution center', 'entity', ['warehouse', 'storage', 'loading dock']),
  make('mixed-use-building', 'Mixed-use building', 'entity', ['mixed use', 'retail', 'residential']),
  make('strip-mall-plaza', 'Strip mall plaza', 'entity', ['strip mall', 'commercial plaza', 'retail']),
  make('mobile-home-park', 'Manufactured home', 'entity', ['mobile home', 'manufactured housing', 'trailer']),
  make('wildland-wui-interface', 'Wildland urban interface', 'inspection', ['wui', 'defensible space', 'wildland']),
  make('fire-hydrant-3d', '3D fire hydrant', 'equipment', ['hydrant', 'fire flow'], 'controlled-hazard'),
  make('propane-tank-3d', '3D propane storage tank', 'hazard', ['propane', 'lpg', 'flammable gas'], 'controlled-hazard'),
  make('ladder-truck-tiller', 'Aerial ladder truck', 'equipment', ['ladder truck', 'tiller', 'apparatus']),
  make('sprinkler-riser-3d', 'Fire sprinkler riser', 'equipment', ['sprinkler', 'riser', 'fire protection']),
  make('knox-key-box-3d', 'Knox key box', 'equipment', ['knox box', 'rapid entry', 'key vault']),
];
